import React from 'react';
import { Skeleton } from '@mui/material';
import { LayersOutlined, StorageOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { bytesToString, formatNumber } from 'js/lib/FormatUtils';
import styles from './FlowKpiSummary.module.scss';

interface FlowKpiSummaryProps {
  records: number;
  errors: number;
  size: number;
  loading: boolean;
  hideRecords?: boolean;
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClass?: string;
  subtitle: string;
  subtitleClass?: string;
  loading: boolean;
}

const KpiCard = ({
  icon,
  label,
  value,
  valueClass = styles.cardValue,
  subtitle,
  subtitleClass = styles.cardSubtitle,
  loading,
}: KpiCardProps) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <span className={styles.cardIcon} aria-hidden>
        {icon}
      </span>
      <span className={styles.cardLabel}>{label}</span>
    </div>
    {loading ? (
      <Skeleton animation="wave" variant="rounded" width="60%" height={32} />
    ) : (
      <span className={valueClass}>{value}</span>
    )}
    <span className={subtitleClass}>{subtitle}</span>
  </div>
);

const FlowKpiSummary = ({
  records,
  errors,
  size,
  loading,
  hideRecords = false,
}: FlowKpiSummaryProps) => {
  const hasErrors = errors > 0;

  return (
    <div className={styles.cards}>
      {!hideRecords && (
        <KpiCard
          icon={<StorageOutlined fontSize="small" />}
          label="Total Records"
          value={formatNumber(records)}
          subtitle="in selected period"
          loading={loading}
        />
      )}
      <KpiCard
        icon={<WarningAmberOutlined fontSize="small" />}
        label="Errors"
        value={formatNumber(errors)}
        valueClass={hasErrors ? `${styles.cardValue} ${styles.errorValue}` : styles.cardValue}
        subtitle={hasErrors ? 'Requires attention' : 'No errors in period'}
        subtitleClass={hasErrors ? styles.errorSubtitle : styles.cardSubtitle}
        loading={loading}
      />
      <KpiCard
        icon={<LayersOutlined fontSize="small" />}
        label="Data Volume"
        value={size > 0 ? bytesToString(size) : '0 B'}
        subtitle="in selected period"
        loading={loading}
      />
    </div>
  );
};

FlowKpiSummary.displayName = 'FlowKpiSummary';

export default FlowKpiSummary;
